import { useEffect, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "coderlity:locale";

export type LocaleRow = {
  code: string;
  name: string;
  native_name: string | null;
  rtl: boolean;
  is_default: boolean;
  enabled: boolean;
  sort_order: number;
};

function readStored(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function applyToDocument(code: string, rtl: boolean) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = code;
  document.documentElement.dir = rtl ? "rtl" : "ltr";
}

/** Lists enabled locales from the DB (cached). */
export function useLocales() {
  return useQuery({
    queryKey: ["locales", "enabled"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locales")
        .select("code,name,native_name,rtl,is_default,enabled,sort_order")
        .eq("enabled", true)
        .order("sort_order")
        .order("code");
      if (error) throw error;
      return (data ?? []) as LocaleRow[];
    },
    staleTime: 5 * 60_000,
  });
}

/** Active locale code (persisted to localStorage). */
export function useLocale() {
  const { data: locales } = useLocales();
  const [code, setCodeState] = useState<string>(() => readStored() ?? "en");

  // Pick a sensible default the first time data loads.
  useEffect(() => {
    if (!locales?.length) return;
    const stored = readStored();
    const exists = stored && locales.some((l) => l.code === stored);
    if (!exists) {
      const def = locales.find((l) => l.is_default) ?? locales[0];
      setCodeState(def.code);
      try { window.localStorage.setItem(STORAGE_KEY, def.code); } catch { /* noop */ }
    }
  }, [locales]);

  // Sync <html lang/dir> whenever code or locale metadata changes.
  useEffect(() => {
    const row = locales?.find((l) => l.code === code);
    applyToDocument(code, !!row?.rtl);
  }, [code, locales]);

  const setLocale = useCallback((next: string) => {
    setCodeState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
    const row = (locales ?? []).find((l) => l.code === next);
    applyToDocument(next, !!row?.rtl);
    // Notify other tabs / components.
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("locale-change", { detail: next }));
    }
  }, [locales]);

  return { code, setLocale, locales: locales ?? [] };
}
